// src/components/page/article/ArticleDetail.tsx
'use client';

import styles from './ArticleDetail.module.scss';
import Link from 'next/link';
import { useMemo, useState, useCallback, useEffect } from 'react';

// 元件
import Avatar from '@/components/ui/avatar/Avatar';
import HeaderContent from '@/components/header/article/HeaderContent';
import { ArticleReplyBar } from '@/app/article/(BottomBar)/BottomBarClient';
import { ViewStat, ReplyStat, CoinStat, ShareStat } from '@/components/page/article/ArticleStats';
import ShareModal from '@/components/ui/modal/ShareModal';
import StatusCardModal, { type StatusKind } from '@/components/ui/modal/StatusCardModal';
import {
  ReceiveBtn,
  GiveMyBtn,
  GiveBtn,
  WishBtn,
  ExchangeBtn,
  SecretBtn,
  ReunionBtn,
  LotteryBtn,
  CollectBtn,
} from '@/components/ui/articleList/ArticleListCoinBtn';
import ArticleMediaCarousel from '@/components/page/article/ArticleMediaCarousel';

// data
import type { Article, ArticleMessage } from '@/model/article';
// import type { ArticleDetailData } from '@/types/article';
import { useArticleMessages } from '@/hooks/useArticleMessages';
import { ArticleService } from '@/libs/services/article';

// 工具
import { formatMessageTimeLabel } from '@/libs/utils/time';

// store
import { useAppSelector } from '@/store/hook';

type Props = {
  articleId: string;
  /** 新版 API 正規化後的完整文章資料 */
  article: Article;
  /** 是否顯示桌機版 HeaderContent（預設 true） */
  showDesktopHeader?: boolean;
  /** 是否顯示底部回覆/互動列（預設 true） */
  showReplyBar?: boolean;
  /**
   * 是否顯示右側「領取/互動」區塊：
   * - 外層已決定好（依 bizType 或 props），這裡只照著顯示
   */
  showClaimActions?: boolean;
  onBeforeEnterPost?: () => void;
};

const fallbackParagraph = '';

type MeInfoLike = {
  user_id?: string | number;
  id?: string | number;
  womo_id?: string | number;
};

/** 依 bizType 決定要顯示哪種互動按鈕 */
function renderBizAction(
  article: Article,
  options?: {
    onBeforeEnterPost?: () => void;
    msgCoinSoldOut?: boolean;
    chipGiveSoldOut?: boolean;
    hasClaimedMsgCoin?: boolean;
    hasClaimedChipGive?: boolean;
  }
) {
  const {
    onBeforeEnterPost,
    msgCoinSoldOut,
    chipGiveSoldOut,
    hasClaimedMsgCoin,
    hasClaimedChipGive,
  } = options ?? {};

  switch (article.bizType) {
    case 'mood':
      return <ReceiveBtn womoId={article.womoId ?? article.author?.id ?? ''} />;

    case 'message_get_coin':
      return (
        <GiveMyBtn
          articleId={article.id}
          coinAvatarSrc={article.author.avatar}
          onBeforeEnterPost={onBeforeEnterPost}
          soldOut={msgCoinSoldOut}
          hasClaimedOverride={hasClaimedMsgCoin}
        />
      );

    case 'coin_give':
      return (
        <GiveBtn
          articleId={article.id}
          coinAvatarSrc={article.chipGive?.typePhoto}
          coinType={article.chipGive?.type}
          onBeforeEnterPost={onBeforeEnterPost}
          soldOut={chipGiveSoldOut}
          hasClaimedOverride={hasClaimedChipGive}
        />
      );

    case 'coin_receive':
      return <WishBtn />;
    case 'coin_exchange':
      return <ExchangeBtn />;
    case 'secret':
      return <SecretBtn />;
    case 'pair':
      return <ReunionBtn />;
    case 'lottery':
      return <LotteryBtn />;
    case 'kol_privilege':
      return <CollectBtn />;
    default:
      return null;
  }
}

/** 留言節點（直接用 ArticleMessage） */
type MessageNode = ArticleMessage;

/* -------------------------------------------------------
 * 單一留言（父留言 / 回覆共用 UI）
 * ----------------------------------------------------- */

type MessageItemProps = {
  node: MessageNode;
  /** 顯示層級：0 = 父留言, 1 = 回覆（第二層 / 第三層…統一這層） */
  depth?: number;

  /** 父留言用：是否顯示「查看 X 則回覆」按鈕 */
  showToggleReplies?: boolean;
  isExpanded?: boolean;
  isLoadingReplies?: boolean;
  hasChildren?: boolean;
  onToggleReplies?: (id: string) => void;

  /** 點「回覆」 */
  onReply: (node: MessageNode) => void;
  /** 點「刪除」 */
  onDelete: (node: MessageNode) => void;
  /** 是否可以刪除（留言者本人） */
  canDelete: boolean;

  /** 若是「回覆某則留言」，在內容前顯示 @對象 */
  replyTargetName?: string;
  replyTargetSlug?: string;

  /**「查看 X 則回覆」中的 X（含第三層以上） */
  replyCountForDisplay?: number;
};

function MessageItem({
  node,
  depth = 0,
  showToggleReplies = false,
  isExpanded = false,
  isLoadingReplies = false,
  hasChildren = false,
  onToggleReplies,
  onReply,
  onDelete,
  canDelete,
  replyTargetName,
  replyTargetSlug,
  replyCountForDisplay,
}: MessageItemProps) {
  const marginLeft = depth > 0 ? 24 : 0;

  const displayName =
    (node.authorName && node.authorName.trim()) || (node.userId ? `用戶 ${node.userId}` : '匿名');

  const userSlug = (node.authorId || node.womoId || node.userId || '').trim?.() ?? '';

  const avatarSrc = node.authorAvatar || '';
  const timeText = node.createTime || '';

  const showReplyPrefix = !!replyTargetName;

  const replyCountText =
    typeof replyCountForDisplay === 'number' ? replyCountForDisplay : (node.childCount ?? 0);

  return (
    <div className="mb-3" style={{ marginLeft, maxWidth: '100%' }}>
      <div className="d-flex align-items-start fs-sm">
        <div className="flex-shrink-0">
          {avatarSrc ? (
            <Link href={userSlug ? `/user/${userSlug}` : '#'}>
              <Avatar src={avatarSrc} size={40} />
            </Link>
          ) : (
            <div className="rounded-circle bg-grey-100" style={{ width: 40, height: 40 }} />
          )}
        </div>

        <div className="d-flex flex-column ms-2 flex-grow-1">
          <div className="d-flex align-items-center">
            <Link href={userSlug ? `/user/profile/${userSlug}` : '#'}>
              <span className="fw-bold">{displayName}</span>
            </Link>
            {timeText && (
              <span className="text-muted fs-sm-2">．{formatMessageTimeLabel(timeText)}</span>
            )}
          </div>

          <p className="py-1 mb-1" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {showReplyPrefix && (
              <Link href={replyTargetSlug ? `/user/${replyTargetSlug}` : '#'}>
                <span className="text-primary me-1">@{replyTargetName}</span>
              </Link>
            )}
            {node.content}
          </p>

          <div className="d-flex align-items-center">
            {/* 回覆 */}
            <button
              type="button"
              className="btn btn-link btn-sm fs-sm-2 p-0 text-grey-800"
              onClick={() => onReply(node)}
            >
              回覆
            </button>

            {/* 刪除（只顯示給留言者本人） */}
            {canDelete && (
              <button
                type="button"
                className="btn btn-link btn-sm fs-sm-2 text-grey-800 p-0 ms-3"
                onClick={() => onDelete(node)}
              >
                刪除
              </button>
            )}

            {/* 父留言的「查看 / 隱藏回覆」 */}
            {showToggleReplies && hasChildren && (
              <button
                type="button"
                className="btn btn-link btn-sm fs-sm-2 p-0 text-grey-800 ms-3"
                onClick={() => onToggleReplies && onToggleReplies(node.id)}
                disabled={isLoadingReplies}
              >
                {isLoadingReplies
                  ? '回覆載入中...'
                  : isExpanded
                    ? '隱藏回覆'
                    : `查看 ${replyCountText} 則回覆`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------
 * ArticleDetail 主元件
 * ----------------------------------------------------- */

export default function ArticleDetail({
  articleId,
  article,
  showDesktopHeader = true,
  showReplyBar = true,
  showClaimActions,
  onBeforeEnterPost,
}: Props) {
  const title = article.title || undefined;

  const meInfo = useAppSelector((state) => state.auth.meInfo) as MeInfoLike | null;

  const myUserId = useMemo<string | number | undefined>(() => {
    if (!meInfo) return undefined;
    return meInfo.user_id ?? meInfo.id;
  }, [meInfo]);

  // ⭐ 文章類型判斷
  const isMessageGetCoinArticle =
    article.articleTypeRaw === 'message_get_coin' || article.bizType === 'message_get_coin';

  const isChipGiveArticle =
    article.articleTypeRaw === 'chip_give' || article.bizType === 'coin_give';

  // message_get_coin：目前沒有領取上限，先預留變數給未來用
  // const messageGetCoinSoldOut = (article.messageGetCoin?.amount ?? 0) <= 0;
  const messageGetCoinSoldOut = false;

  // chip_give：用 amount 判斷是否已發完
  const chipGiveSoldOut = useMemo(
    () => (article.chipGive?.amount ?? 0) <= 0,
    [article.chipGive?.amount]
  );

  // ⭐ 同一會員同一篇 message_get_coin / chip_give 只能領一次（前端記錄）
  const [hasClaimedMsgCoin, setHasClaimedMsgCoin] = useState(false);
  const [hasClaimedChipGive, setHasClaimedChipGive] = useState(false);

  // ⭐ 成功獲得籌碼視窗使用的頭像
  const [rewardAvatarSrc, setRewardAvatarSrc] = useState<string | undefined>(
    article.chipGive?.typePhoto ||
      article.messageGetCoin?.Photo ||
      article.author?.avatar ||
      undefined
  );

  // 初始化：message_get_coin 是否已領
  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (!myUserId || !isMessageGetCoinArticle) {
      setHasClaimedMsgCoin(false);
      return;
    }

    const key = `msgCoinClaimed:${articleId}:${myUserId}`;
    const val = window.localStorage.getItem(key);
    setHasClaimedMsgCoin(val === '1');
  }, [articleId, myUserId, isMessageGetCoinArticle]);

  // 初始化：chip_give 是否已領
  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (!myUserId || !isChipGiveArticle) {
      setHasClaimedChipGive(false);
      return;
    }

    const key = `chipGiveClaimed:${articleId}:${myUserId}`;
    const val = window.localStorage.getItem(key);
    setHasClaimedChipGive(val === '1');
  }, [articleId, myUserId, isChipGiveArticle]);

  // StatusCardModal 狀態
  const [statusOpen, setStatusOpen] = useState(false);
  const [statusKind, setStatusKind] = useState<StatusKind>('success');

  const openStatus = (kind: StatusKind) => {
    setStatusKind(kind);
    setStatusOpen(true);
  };

  // 從 article.id 拆出 table / table_id 給留言 / 分享 API 用
  const { tableForMessage, tableIdForMessage } = useMemo(() => {
    const table = article.table || 'collection';
    const id = article.id || '';

    let tableId = '';

    const prefix = `${table}_`;
    if (id.startsWith(prefix)) {
      tableId = id.slice(prefix.length);
    } else {
      const parts = id.split('_');
      tableId = parts[parts.length - 1] || '';
    }

    return { tableForMessage: table, tableIdForMessage: tableId };
  }, [article.id, article.table]);

  const contentParagraphs = useMemo(() => {
    const raw = article.content || fallbackParagraph;
    return raw.split(/\n{2,}/g);
  }, [article.content]);

  const stats = article.stats;
  const viewCount = stats?.views ?? 0;
  const coinCount = stats?.favorites ?? 0;

  // 分享數：用 state 控制，方便點擊後 +1
  const [shareCount, setShareCount] = useState<number>(stats?.shares ?? 0);

  // 當後端帶進來的 stats.shares 變動時，同步到 state
  useEffect(() => {
    setShareCount(stats?.shares ?? 0);
  }, [stats?.shares]);

  // 監聽 Header（DropDownMenu）分享事件：用來讓 shareCount +1
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handler = (event: Event) => {
      const e = event as CustomEvent<{ id?: string }>;
      const targetId = e.detail?.id;

      // 若事件沒帶 id，或 id 與當前文章相符，才 +1
      if (!targetId || targetId === article.id) {
        setShareCount((prev) => prev + 1);
      }
    };

    window.addEventListener('article-share', handler as EventListener);

    return () => {
      window.removeEventListener('article-share', handler as EventListener);
    };
  }, [article.id]);

  const [openShare, setOpenShare] = useState(false);

  const shareUrl = useMemo(() => {
    if (typeof window !== 'undefined') {
      if (article?.id) {
        // 依照當前 origin 組絕對網址（local / prod 都 OK）
        return new URL(`/article/${article.id}`, window.location.origin).toString();
      }
      return window.location.href;
    }
    // SSR fallback：給個正式網域（依你專案調整）
    return article?.id ? `https://www.womo.com.tw/article/${article.id}` : '';
  }, [article?.id]);

  const shareTitle = useMemo(() => {
    if (article?.title && article.title.trim()) return article.title.trim();
    if (article?.author?.name && article.author.name.trim()) {
      return `${article.author.name.trim()} 的籌碼心情`;
    }
    return 'WOMO 的籌碼心情';
  }, [article?.title, article?.author?.name]);

  const shareDesc = useMemo(() => {
    if (!article?.content) return '';
    return article.content
      .replace(/<[^>]+>/g, ' ') // 去掉 HTML tag
      .replace(/\s+/g, ' ')
      .slice(0, 160);
  }, [article?.content]);

  // 內文區分享按鈕：分享 +1 ＋ 開 ShareModal
  const handleShareClick = async () => {
    // 沒有 table 或 tableId 的情況就只開 ShareModal
    if (!tableForMessage || !tableIdForMessage) {
      setOpenShare(true);
      return;
    }

    try {
      // 樂觀更新：先把數字 +1
      setShareCount((prev) => prev + 1);

      await ArticleService.share({
        table: tableForMessage,
        table_id: tableIdForMessage,
      });
    } catch (err) {
      console.error('[ArticleDetail] share error:', err);
      // 發生錯誤就回退數字
      setShareCount((prev) => (prev > 0 ? prev - 1 : 0));
      alert('分享失敗，請稍後再試');
    } finally {
      setOpenShare(true);
    }
  };

  // 留言數：優先用後端統計，其次再 fallback
  const replyCountFromStats = stats?.replies;
  const replyCountFromArticle = article.messages?.length ?? 0;

  const {
    parents,
    childrenMap,
    loadingParents,
    loadingRepliesMap,
    error: messagesError,
    hasMoreParents,
    totalParents,
    loadMoreParents,
    loadRepliesFor,
    reloadRepliesFor,
    reloadParents,
  } = useArticleMessages(tableForMessage, tableIdForMessage);

  // 建立 messageMap：id -> ArticleMessage，方便往上追 roots
  const messageMap = useMemo(() => {
    const map = new Map<string, ArticleMessage>();
    parents.forEach((p) => map.set(p.id, p));
    Object.values(childrenMap).forEach((list) => {
      list.forEach((m) => map.set(m.id, m));
    });
    return map;
  }, [parents, childrenMap]);

  // 從任意留言 id 找到這串 thread 的最上層父留言 id
  const findThreadRootId = useCallback(
    (msgId: string | undefined | null): string | null => {
      if (!msgId) return null;
      let currentId = String(msgId);
      const visited = new Set<string>();

      while (currentId && !visited.has(currentId)) {
        visited.add(currentId);
        const msg = messageMap.get(currentId);
        if (!msg) break;

        if (!msg.parentId || msg.parentId === '0') {
          return msg.id;
        }

        currentId = msg.parentId;
      }

      return null;
    },
    [messageMap]
  );

  const replyCount =
    typeof replyCountFromStats === 'number'
      ? replyCountFromStats
      : typeof totalParents === 'number'
        ? totalParents
        : parents.length || replyCountFromArticle;

  // 展開/收合：記錄「哪些父留言 id 已展開」
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const handleToggleReplies = (id: string) => {
    void loadRepliesFor(id);

    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // 判斷是否可刪除
  const canDeleteFor = useCallback(
    (msg: ArticleMessage) => {
      if (!meInfo) return false;

      const myUid = meInfo.user_id ?? meInfo.id;
      const myWomoId = meInfo.womo_id;

      if (myUid != null && String(myUid) === msg.userId) return true;
      if (myWomoId != null && msg.womoId && String(myWomoId) === String(msg.womoId)) return true;

      return false;
    },
    [meInfo]
  );

  // 刪除留言：只重抓該 thread 或父列表，避免整個區塊抖動
  const handleDelete = async (node: MessageNode) => {
    if (!tableForMessage) return;
    if (!canDeleteFor(node)) {
      alert('只能刪除自己發的留言');
      return;
    }

    const ok = window.confirm('確定要刪除這則留言嗎？');
    if (!ok) return;

    try {
      await ArticleService.deleteMessage(tableForMessage, node.id);

      const isRoot = !node.parentId || node.parentId === '0';

      if (isRoot) {
        // 刪的是父留言：重抓父列表
        await reloadParents();
      } else {
        // 刪的是回覆：找這串留言的 root，重抓那一串
        const rootId = findThreadRootId(node.id);
        if (rootId) {
          await reloadRepliesFor(rootId);
          setExpandedIds((prev) => {
            const next = new Set(prev);
            next.add(rootId); // 保持這串 thread 展開
            return next;
          });
        }
      }
    } catch (err) {
      console.error('[ArticleDetail] deleteMessage error:', err);
      alert('刪除失敗，請稍後再試');
    }
  };

  // 回覆目標 + 送出留言
  const [replyTarget, setReplyTarget] = useState<{
    id: string;
    authorName?: string;
    threadRootId?: string;
  } | null>(null);

  const [sending, setSending] = useState(false);

  // 點「回覆」：會把 threadRootId 一起記起來
  const handleReply = (node: MessageNode, rootIdFromParent?: string) => {
    const threadRootId = rootIdFromParent || findThreadRootId(node.id) || node.id;

    setReplyTarget({
      id: node.id,
      authorName:
        (node.authorName && node.authorName.trim()) ||
        (node.userId ? `用戶 ${node.userId}` : '留言'),
      threadRootId,
    });

    setTimeout(() => {
      const el = document.getElementById('reply');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        (el as HTMLTextAreaElement).focus();
      }
    }, 0);
  };

  const handleCancelReply = () => {
    setReplyTarget(null);
  };

  // 送出留言（主留言 or 回覆）
  const handleSubmitComment = async (content: string, parentIdFromBar?: string) => {
    const trimmed = content.trim();
    if (!trimmed) return;
    if (!tableForMessage || !tableIdForMessage) return;
    if (sending) return;

    const currentReplyTarget = replyTarget;
    const parentId = parentIdFromBar || currentReplyTarget?.id;

    // 這次送出的留言串 root id（有回覆則為父留言）
    const threadRootIdForThisSubmit = parentId
      ? currentReplyTarget?.threadRootId || findThreadRootId(parentId) || parentId
      : null;

    // ================================
    //  自己文章 / 自己籌碼不能領判斷
    // ================================

    // 文章作者（同時也是 message_get_coin 的籌碼主人）
    const msgCoinOwnerId = article.author?.id;

    // chip_give 的籌碼主人（例如 KOL 綠籌碼 / 金籌碼）
    const chipMeta = article.chipGive;
    const chipCoinId = chipMeta?.typeId;

    // 👉 判斷「這是不是我發的文章」
    const isMyArticle =
      msgCoinOwnerId != null && myUserId != null && String(myUserId) === String(msgCoinOwnerId);

    // 👉 判斷「這顆 message_get_coin 籌碼是不是我的」（通常會等同於 isMyArticle）
    const isSelfMsgCoin =
      myUserId != null && msgCoinOwnerId != null && String(myUserId) === String(msgCoinOwnerId);

    // 👉 判斷「這顆 chip_give 的籌碼是不是我的」
    const isSelfChipGive =
      myUserId != null && chipCoinId != null && String(myUserId) === String(chipCoinId);

    // ✅ 最終：只要是「自己文章」或「自己的籌碼」，一律不能領
    const blockSelfClaim = isMyArticle || isSelfMsgCoin || isSelfChipGive;

    // ================================
    // 這一次是否有資格「嘗試領籌碼」
    // ================================
    const canClaimMessageCoin =
      !!myUserId &&
      isMessageGetCoinArticle &&
      !hasClaimedMsgCoin &&
      typeof window !== 'undefined' &&
      !blockSelfClaim; // 👈 自己文章 / 自己籌碼 → false

    const canClaimChipGive =
      !!myUserId &&
      isChipGiveArticle &&
      !hasClaimedChipGive &&
      typeof window !== 'undefined' &&
      !blockSelfClaim; // 👈 自己文章 / 自己籌碼 → false

    // 組 payload（可能會帶 message_get_coin / chip_give_id）
    const basePayload: {
      table: string;
      table_id: string | number;
      content: string;
      parent_id?: string | number;
      message_get_coin?: 0 | 1;
      chip_give_id?: string | number;
    } = {
      table: tableForMessage,
      table_id: tableIdForMessage,
      content: trimmed,
    };

    if (parentId) {
      basePayload.parent_id = parentId;
    }

    if (canClaimMessageCoin) {
      basePayload.message_get_coin = 1;
    }

    if (canClaimChipGive && article.chipGive?.id) {
      basePayload.chip_give_id = article.chipGive.id;
    }

    const applyAfterSuccess = async (withReward: boolean) => {
      // 清空「回覆目標」
      setReplyTarget(null);

      // 依情境重抓留言
      if (threadRootIdForThisSubmit) {
        await reloadRepliesFor(threadRootIdForThisSubmit);
        setExpandedIds((prev) => {
          const next = new Set(prev);
          next.add(threadRootIdForThisSubmit);
          return next;
        });
      } else {
        await reloadParents();
      }

      // 有拿到籌碼才開成功彈窗
      if (withReward) {
        openStatus('success');
      }
    };

    try {
      setSending(true);

      // 第一次嘗試（可能帶領籌碼參數）
      await ArticleService.createMessage(basePayload);

      let hasReward = false;

      // ⭐ 只要第一次嘗試成功，且我們本來就有嘗試領籌碼，就視為「領成功」
      if (typeof window !== 'undefined' && myUserId) {
        // chip_give：留言贈幣
        if (canClaimChipGive && isChipGiveArticle && article.chipGive) {
          const key = `chipGiveClaimed:${articleId}:${myUserId}`;
          window.localStorage.setItem(key, '1');
          setHasClaimedChipGive(true);
          hasReward = true;

          const meta = article.chipGive;
          const bucket = meta.type === 'kol_pay' ? 'pay' : 'free';

          setRewardAvatarSrc(meta.typePhoto || article.author?.avatar);

          window.dispatchEvent(
            new CustomEvent('coin-received', {
              detail: {
                source: 'chip_give',
                bucket, // 'free' | 'pay'
                coinId: meta.typeId,
                coinType: meta.type,
                coinName: meta.typeName,
                coinPhoto: meta.typePhoto,
                amount: 1,
              },
            })
          );
        }

        // message_get_coin：留言就送 KOL 綠籌碼
        if (canClaimMessageCoin && isMessageGetCoinArticle) {
          const key = `msgCoinClaimed:${articleId}:${myUserId}`;
          window.localStorage.setItem(key, '1');
          setHasClaimedMsgCoin(true);
          hasReward = true;

          const meta = article.messageGetCoin;
          const coinName = (meta && meta.name) || article.author?.name || '';
          const coinPhoto = meta?.Photo || article.author?.avatar || '';
          const coinType = meta?.type || 'kol';
          const coinOwnerId = article.author?.id;

          setRewardAvatarSrc(coinPhoto || article.author?.avatar);

          if (coinOwnerId) {
            window.dispatchEvent(
              new CustomEvent('coin-received', {
                detail: {
                  source: 'message_get_coin',
                  bucket: 'free',
                  coinId: coinOwnerId,
                  coinType,
                  coinName,
                  coinPhoto,
                  amount: 1,
                },
              })
            );
          }
        }
      }

      await applyAfterSuccess(hasReward);
    } catch (err) {
      console.error('[ArticleDetail] createMessage error (first attempt):', err);

      const triedClaimCoin = basePayload.message_get_coin === 1 || !!basePayload.chip_give_id;

      // ⭐ 如果這次有嘗試領籌碼，但後端回錯（常見：已領過 / 沒數量），就改成「純留言」再送一次
      if (triedClaimCoin) {
        if (typeof window !== 'undefined' && myUserId) {
          // 標記「已經試過領」，避免之後再夾籌碼參數
          if (basePayload.message_get_coin === 1 && isMessageGetCoinArticle) {
            const key = `msgCoinClaimed:${articleId}:${myUserId}`;
            window.localStorage.setItem(key, '1');
            setHasClaimedMsgCoin(true);
          }
          if (basePayload.chip_give_id && isChipGiveArticle) {
            const key = `chipGiveClaimed:${articleId}:${myUserId}`;
            window.localStorage.setItem(key, '1');
            setHasClaimedChipGive(true);
          }
        }

        try {
          const fallbackPayload: {
            table: string;
            table_id: string | number;
            content: string;
            parent_id?: string | number;
          } = {
            table: tableForMessage,
            table_id: tableIdForMessage,
            content: trimmed,
          };

          if (parentId) {
            fallbackPayload.parent_id = parentId;
          }

          // 第二次只當作「純留言」，不再帶 message_get_coin / chip_give_id
          await ArticleService.createMessage(fallbackPayload);

          // 純留言成功：不顯示獲得籌碼彈窗
          await applyAfterSuccess(false);
          return;
        } catch (err2) {
          console.error('[ArticleDetail] createMessage error (fallback attempt):', err2);
        }
      }

      alert('留言送出失敗，請稍後再試');
    } finally {
      setSending(false);
    }
  };

  // 進到這篇文章時，若是從列表的 GiveBtn 點進來，就自動 focus 留言框
  useEffect(() => {
    if (typeof window === 'undefined') return;

    try {
      const targetId = window.sessionStorage.getItem('focusReplyForArticle');
      if (!targetId || targetId !== articleId) return;

      const el = document.getElementById('reply') as HTMLTextAreaElement | null;
      if (el) {
        el.focus();
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      window.sessionStorage.removeItem('focusReplyForArticle');
    } catch (err) {
      console.warn('[ArticleDetail] auto focus reply failed:', err);
    }
  }, [articleId]);

  const actionEl = useMemo(
    () =>
      renderBizAction(article, {
        onBeforeEnterPost,
        msgCoinSoldOut: messageGetCoinSoldOut,
        chipGiveSoldOut,
        hasClaimedMsgCoin,
        hasClaimedChipGive,
      }),
    [
      article,
      onBeforeEnterPost,
      messageGetCoinSoldOut,
      chipGiveSoldOut,
      hasClaimedMsgCoin,
      hasClaimedChipGive,
    ]
  );

  // 是否有任何媒體（圖片 / YouTube / 抖音 / 音訊 / 本機影片）
  const hasAnyMedia =
    (Array.isArray(article.covers) && article.covers.length > 0) ||
    !!article.cover ||
    !!article.url ||
    !!article.tiktokId ||
    !!article.music ||
    !!(article.shortData && article.shortData.length > 0);

  return (
    <>
      {/* 桌機顯示區（可選） */}
      {showDesktopHeader && (
        <div className="d-none d-md-block">
          <HeaderContent
            backBtnShow="never"
            articleId={articleId}
            article={article}
            sourceLabel="detail-desktop"
          />
        </div>
      )}

      {/* 標題（若有） */}
      {title && (
        <section className="container-fluid mt-3">
          <h1 className="h5 fw-bold mb-0">{title}</h1>
        </section>
      )}

      {/* 文字區 */}
      {contentParagraphs
        ?.map((p) => (typeof p === 'string' ? p.trim() : ''))
        .filter((p) => p.replace(/<[^>]*>/g, '').trim().length > 0).length > 0 && (
        <section className="container-fluid">
          <div className={`mt-2 mb-5 ${styles.articleContent}`}>
            {contentParagraphs.map((p, idx) => (
              <p key={idx} className="mb-3" dangerouslySetInnerHTML={{ __html: p }} />
            ))}
          </div>
        </section>
      )}

      {/* 多媒體區：圖片 / YouTube / 抖音 / 音訊 / 本機影片 */}
      {hasAnyMedia && (
        <section className="mb-4">
          <ArticleMediaCarousel article={article} variant="detail" className={styles.imgWrapper} />
        </section>
      )}

      {/* 領取 / 互動區塊（依 bizType 顯示對應按鈕） */}
      {showClaimActions && actionEl && (
        <section className="container-fluid mt-3 mb-4">{actionEl}</section>
      )}

      <div className="container-fluid flex-grow-1">
        {/* 數據區 */}
        <section className="mt-3 mb-5 border-bottom border-grey pb-2">
          <ul className="d-flex align-items-center text-muted">
            <li className="d-flex align-items-center">
              <ViewStat value={viewCount} />
            </li>
            <li className="d-flex align-items-center ms-3">
              <ReplyStat value={replyCount} />
            </li>
            <li className="d-flex align-items-center ms-3">
              <CoinStat value={coinCount} />
            </li>
            <li className="d-flex align-items-center ms-3">
              <button
                type="button"
                className="btn p-0 text-muted d-inline-flex align-items-center"
                aria-label={`分享（共 ${shareCount} 次）`}
                onClick={handleShareClick}
              >
                <ShareStat value={shareCount} />
              </button>
            </li>
          </ul>
        </section>

        {/* 留言區 */}
        <section className="my-5">
          {loadingParents && parents.length === 0 && (
            <p className="text-muted fs-sm mb-3">留言載入中...</p>
          )}

          {messagesError && !loadingParents && (
            <p className="text-danger fs-sm mb-3">{messagesError}</p>
          )}

          {!loadingParents && !messagesError && parents.length === 0 && (
            <p className="text-grey fs-sm mb-0">還沒有人留言，快來留言吧！</p>
          )}

          {!messagesError &&
            parents.map((parent) => {
              const replies = childrenMap[parent.id] ?? [];
              const isExpanded = expandedIds.has(parent.id);

              // 👉 若已載入，replyCountForDisplay = replies.length（含第三層以上）
              //    若還沒載過，則先用後端 childCount 當預設
              const repliesCountFromMap = replies.length;
              const replyCountForDisplay =
                repliesCountFromMap > 0 ? repliesCountFromMap : (parent.childCount ?? 0);

              const hasChildren = replyCountForDisplay > 0;

              return (
                <div key={parent.id}>
                  {/* 父留言 */}
                  <MessageItem
                    node={parent}
                    depth={0}
                    showToggleReplies={hasChildren}
                    isExpanded={isExpanded}
                    isLoadingReplies={!!loadingRepliesMap[parent.id]}
                    hasChildren={hasChildren}
                    onToggleReplies={handleToggleReplies}
                    onReply={(node) => handleReply(node, parent.id)}
                    onDelete={handleDelete}
                    canDelete={canDeleteFor(parent)}
                    replyCountForDisplay={replyCountForDisplay}
                  />

                  {/* 回覆：全部展在第二層；第三層以上用 @回覆對象 */}
                  {isExpanded &&
                    replies.map((reply) => {
                      const isDirectReplyToParent = reply.parentId === parent.id;
                      let replyTargetName: string | undefined;
                      let replyTargetSlug: string | undefined;

                      if (!isDirectReplyToParent && reply.parentId) {
                        const target = messageMap.get(reply.parentId);
                        if (target) {
                          const name =
                            (target.authorName && target.authorName.trim()) ||
                            (target.userId ? `用戶 ${target.userId}` : '留言');
                          const slug = (target.womoId || target.userId || '').trim?.() ?? '';
                          replyTargetName = name;
                          replyTargetSlug = slug || undefined;
                        }
                      }

                      return (
                        <MessageItem
                          key={reply.id}
                          node={reply}
                          depth={1} // 統一第二層
                          showToggleReplies={false}
                          isExpanded={false}
                          isLoadingReplies={false}
                          hasChildren={false}
                          onReply={(node) => handleReply(node, parent.id)}
                          onDelete={handleDelete}
                          canDelete={canDeleteFor(reply)}
                          replyTargetName={replyTargetName}
                          replyTargetSlug={replyTargetSlug}
                        />
                      );
                    })}
                </div>
              );
            })}

          {/* 載入更多父留言 */}
          {hasMoreParents && !messagesError && (
            <div className="text-center mt-3">
              <button
                type="button"
                className="btn btn-link fs-sm text-muted"
                onClick={loadMoreParents}
                disabled={loadingParents}
              >
                {loadingParents ? '載入中...' : '載入更多留言'}
              </button>
            </div>
          )}
        </section>
      </div>

      {/* 回覆鍵 + 互動列（可選） */}
      {showReplyBar && (
        <div className={`container-fluid bottom-0 bg-light py-2 z-50 ${styles.showReplyBar}`}>
          <ArticleReplyBar
            articleId={articleId}
            showClaimActions={showClaimActions}
            replyingTo={replyTarget}
            onCancelReply={handleCancelReply}
            onSubmit={handleSubmitComment}
          />
        </div>
      )}

      {/* 分享 Modal*/}
      <ShareModal
        open={openShare}
        onClose={() => setOpenShare(false)}
        title="分享文章"
        url={shareUrl || undefined}
        shareTitle={shareTitle}
        shareDescription={shareDesc || undefined}
      />

      {/* 成功領取籌碼彈窗 */}
      <StatusCardModal
        open={statusOpen}
        onClose={() => setStatusOpen(false)}
        status={statusKind}
        actionsOverride={{
          error: [
            { key: 'home', text: '回首頁', href: '/', className: 'btn-red' },
            { key: 'help', text: '聯絡客服', href: '/help', className: 'btn-outline-secondary' },
          ],
        }}
        title={`恭喜您獲得${
          article.bizType === 'message_get_coin'
            ? article.author?.name
            : article.chipGive?.type === 'womo'
              ? '金'
              : article.chipGive?.typeName
        }籌碼！`}
        color={
          article.chipGive?.type === 'tcoin' ||
          article.chipGive?.type === 'kol' ||
          article.bizType === 'message_get_coin'
            ? 'green' // 滿足 'tcoin', 'kol', 或 MessageGetCoin 條件時
            : article.chipGive?.type === 'kol_pay'
              ? 'red' // 滿足 'kol_pay' 條件時
              : 'yellow' // 其他所有情況 (例如: 'womo' 或 undefined)
        }
        avatarSrc={rewardAvatarSrc ?? article.chipGive?.typePhoto ?? article.author?.avatar}
        scale={1.2}
        delayMs={80}
        /* 外觀 */
        dialogSize="sm"
        closeOnBackdrop
        lockScroll
        showCloseButton
      />
    </>
  );
}
